// The thumb dock of mockup round 082 (R1749; ui-map-interface.md §12.0),
// as pure markup: the two caps of the telephone tab bar and the two marks
// of the signal grammar of round 080 group C that the dock draws. The
// overlay owns the state and the presses; this module reads no field of it.
//
// The signal grammar takes no gold (round 080): ATTENTION is a solid light
// count, or a light dot where no count fits. A signal reads by shape too, so
// the count is a filled capsule and the dot a filled circle, each with a
// dark ring that cuts it from the cap under it.

import { esc } from '../esc';
import { FLEET_MARKS } from '../../gen/presentation';
import { inbox as IB, overlay as S, squad as SQ } from '../../gen/strings';
import type { SquadCell } from '../options/kit';
import { devBrand, signSVGElement } from '../sign';
import { calloutTag } from '../welcome';
import { lineIcon } from '../lineicon';

/** The line icons of the dock, in a 24-unit box, drawn with the stroke of
 *  the session buttons of the OPTIONS menu (R1740). */
const ICON = {
  // A tray: the INBOX.
  inbox: '<path d="M5.5 5h13L20.5 13v6h-17v-6z"/><path d="M3.5 13h5l1.5 2.5h4l1.5-2.5h5"/>',
  // Three lines: the MENU.
  menu: '<path d="M5 7h14M5 12h14M5 17h14"/>',
  // A cross: the MENU cap while the OPTIONS menu stands (R1754).
  close: '<path d="M6.5 6.5l11 11M17.5 6.5l-11 11"/>',
  // A chevron to the left: the INBOX cap on a page of the menu (R1754).
  back: '<path d="M14.5 5.5L8 12l6.5 6.5"/>',
  // Three sliders: the OPTIONS of a desktop.
  sliders: '<path d="M4 7h10M18 7h2M4 12h3M11 12h9M4 17h12M20 17h0"/>'
    + '<circle cx="16" cy="7" r="2"/><circle cx="9" cy="12" r="2"/><circle cx="18" cy="17" r="2"/>',
  // Two brackets: the DEV lane.
  dev: '<path d="M9 7l-5 5 5 5M15 7l5 5-5 5"/>',
  // An arrow up: the new build.
  up: '<path d="M12 19V5.5M6.5 11L12 5.5l5.5 5.5"/>',
  // A turning arrow: RESTART.
  restart: '<path d="M19 12a7 7 0 1 1-2.05-4.95"/><path d="M19 4.5V8h-3.5"/>',
  // Two chevrons up: the rank, as the commander row of the OPTIONS menu
  // draws it (R1757).
  rank: '<path d="M6 13l6-5 6 5M6 18.5l6-5 6 5"/>',
} as const;

/** The rank cell in words (R2176): the Honour rank and the place in the
 *  season table, so `Drifter #8` reads whole on a press and to a screen
 *  reader. */
function rankLabel(title: string, rank: number): string {
  return rank > 0 ? S.bar_rank_label({ title, rank }) : S.bar_rank_label_new({ title });
}

/** The name of one icon of the dock. */
type DockIcon = keyof typeof ICON;

/** One line icon, `px` square, hidden from a screen reader. */
export function dockIcon(name: DockIcon, px = 18): string {
  return lineIcon('dic', px, ICON[name]);
}

/** The solid light count of ATTENTION, or nothing for a count of 0. A
 *  count over 99 reads `99+`. */
export function lightCount(n: number): string {
  if (!(n > 0)) return '';
  return `<span class="lcount">${n > 99 ? '99+' : n}</span>`;
}

/** The light dot of ATTENTION where no count fits: a new build. */
export function lightDot(on: boolean): string {
  return on ? '<i class="ldot"></i>' : '';
}

/**
 * The inside of one cap of the telephone tab bar: the icon over the word,
 * then the count or the dot. The cap itself is a fixed button of the
 * overlay; its act and its title never change.
 */
export function capHtml(icon: DockIcon, word: string, o: { count?: number; dot?: boolean } = {}): string {
  return dockIcon(icon) + `<span class="cw">${esc(word)}</span>`
    + lightCount(o.count ?? 0) + lightDot(o.dot ?? false);
}

/** The squad chip of the dock head: the squad cell of the commander row
 *  of the OPTIONS menu (R1757), with the pulse of a squad change. */
export interface DockSquad extends SquadCell {
  /** The age of the pulse of a squad change in ms, or null with none. */
  flashMs: number | null;
}

/** What the dock head of a desktop shows (R1749, card 01 of round 082). */
export interface DockHeadView {
  /** The commander's name, as the hello states it. */
  name: string;
  /** The reader's squad, or null with no squad. */
  squad: DockSquad | null;
  /** The Honour title of the seat, or '' before the hello names one. */
  title: string;
  /** The place on the season ladder, or 0 before the first board. */
  rank: number;
  /** The autopilot keeps this empire under standing orders (R1256). */
  standing: boolean;
  /** The unread count of the INBOX. */
  inbox: number;
  /** Which cell's panel stands: the INBOX in the dock, the OPTIONS menu,
   *  the dev panel. */
  open: { inbox: boolean; opts: boolean; dev: boolean };
  /** An admin seat: the DEV cell, and the new build of a `-watch-git`
   *  server that is behind its branch (R89). Null on every other seat. */
  dev: { behind: number; build: string; title: string } | null;
  /** The welcome card stands: OPTIONS wears its tag 2 (R1572). */
  called: boolean;
}

/** One cell of the dock head: the icon over the word, then the count. A
 *  cell whose panel stands is lit. */
function headCell(o: {
  icon: DockIcon; word: string; attrs: string; title: string; on: boolean;
  count?: number; called?: boolean;
}): string {
  const cls = `dh-act${o.on ? ' on' : ''}${o.called ? ' callout' : ''}`;
  return `<button class="${cls}" ${o.attrs} title="${esc(o.title)}">${o.called ? calloutTag(2) : ''}`
    + `${dockIcon(o.icon)}<span>${esc(o.word)}</span>${lightCount(o.count ?? 0)}</button>`;
}

/**
 * The squad chip of the dock head (R1767, card 17 of round 082): the squad
 * mark, the clan tag in brackets, the squad number and the head count with
 * the "n in" of the window commitments (R3279),
 * then the cup mark beside the chip, outside every control (card 09 of
 * round 078: a press on the mark opens its tip). The chip names no full
 * clan name; the roster card names it. A pickup squad names no clan. The
 * press opens the roster card under the chip.
 */
export function dockSquadHtml(q: DockSquad): string {
  const cls = `dh-chip sq${q.pickup ? ' pick' : ''}${q.flashMs !== null ? ' flash' : ''}`;
  const pulseMs = Math.round(FLEET_MARKS.squadChange.pulseSec * 1000);
  const delay = q.flashMs !== null
    ? ` style="animation-duration:${pulseMs}ms;animation-delay:-${Math.round(q.flashMs)}ms"` : '';
  const words = q.pickup
    ? `<span class="dh-sn">${esc(SQ.chip_pickup)}</span>`
    : (q.tag ? `<b>[${esc(q.tag)}]</b>` : '')
      + `<span class="dh-sn">${esc(SQ.chip_squad({ n: q.n }))}</span>`;
  return `<button class="${cls}" data-act="squad" title="${esc(SQ.chip_title)}"${delay}>${q.icon}`
    + `<span class="dh-cw">${words}<span class="dh-ct">· ${q.count}${q.inWord ? ` · ${esc(q.inWord)}` : ''}</span></span></button>${q.cup}`;
}

/**
 * The dock head of a desktop (R1749, card 01 of round 082): the sign and
 * the name, whose press centres the map on the hold; the INBOX, OPTIONS
 * and DEV cells; under them the squad chip with its cup mark, the rank
 * with its icon and the standing-orders tag (R1767); and, on an admin seat
 * whose dev lane is behind its branch, the new-build line with RESTART. A telephone draws none of
 * it: there the commander row stands in the OPTIONS menu.
 */
export function dockHeadHtml(v: DockHeadView): string {
  const home = `<button class="dh-home" data-act="home" title="${esc(S.bar_home_title)}">`
    + `<span class="dh-sign">${signSVGElement(40, { id: 'dockhead', corner: devBrand() }, 'aria-hidden="true" focusable="false"')}</span>`
    + `<span class="dh-nm"><b>${esc(v.name)}</b><small>${esc(S.head_hold)}</small></span></button>`;
  // A screen reader hears the count with the cell: "INBOX, 3 unread"
  // (inbox.md §6, audit P13).
  const label = v.inbox > 0 ? ` aria-label="${esc(IB.cap_label({ n: v.inbox }))}"` : '';
  const inbox = headCell({
    icon: 'inbox', word: S.head_inbox, attrs: `data-act="meta" data-tab="INBOX"${label}`,
    title: S.bar_inbox_title, on: v.open.inbox, count: v.inbox,
  });
  const opts = headCell({
    icon: 'sliders', word: S.head_opts, attrs: 'data-act="opts"',
    title: S.bar_opts_title, on: v.open.opts, called: v.called,
  });
  const dev = v.dev
    ? headCell({
      icon: 'dev', word: S.head_dev, attrs: 'data-act="admintoggle"',
      title: S.bar_dev_title, on: v.open.dev, count: v.dev.behind,
    })
    : '';
  const rank = v.title === '' ? ''
    // The cell shows `Drifter #8`; its name says it in words (R2176).
    : `<button class="dh-chip dh-rk" data-act="meta" data-tab="STANDINGS" title="${esc(rankLabel(v.title, v.rank))}"`
      + ` aria-label="${esc(rankLabel(v.title, v.rank))}">`
      + `${dockIcon('rank', 13)}<span>${esc(v.title)}${v.rank > 0 ? ` <b>#${v.rank}</b>` : ''}</span></button>`;
  const standing = v.standing
    ? `<button class="dh-chip dh-so" data-act="standingopts" title="${esc(S.bar_standing_title)}">${esc(S.bar_standing)}</button>` : '';
  const row2 = (v.squad ? dockSquadHtml(v.squad) : '') + rank + standing;
  const build = v.dev && v.dev.behind > 0
    ? `<div class="dh-dvl" title="${esc(v.dev.title)}"><span class="dh-upm">${dockIcon('up', 11)}</span>`
      + `<span class="dh-dvt"><b>${esc(S.head_commits({ n: v.dev.behind }))}</b>`
      + `<small>${esc(S.head_build({ build: v.dev.build }))}</small></span>`
      + `<button class="dh-rst" data-act="adm" data-op="saverestart">${dockIcon('restart', 14)}`
      + `${esc(S.dev_restart)}</button></div>`
    : '';
  return `<div class="dh-r1">${home}<span class="dh-acts">${inbox}${opts}${dev}</span></div>`
    + (row2 === '' ? '' : `<div class="dh-r2">${row2}</div>`)
    + build;
}
